// apps/web/src/pages/admin/MechanicGaragesPage.tsx

import React, { useState } from 'react';
import { trpc } from '@/trpc/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  Wrench, Search, ChevronLeft, ChevronRight, Loader2, Edit, Trash2, 
  Plus, MapPin, Phone, Mail, CheckCircle, XCircle, Terminal, Info as InfoIcon,
  Globe, Clock
} from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { 
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, 
  DialogFooter, DialogTrigger 
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { District } from '@logistics-platform/types';
import MapPicker from '@/components/MapPicker';

interface MechanicGarage {
  id: string;
  name: string;
  phone: string;
  phoneTwo?: string | null;
  email?: string | null;
  address?: string | null;
  district: District;
  latitude?: number | null;
  longitude?: number | null;
  isActive: boolean;
  isVerified: boolean;
  services: string[];
  specialties: string[];
  description?: string | null;
  website?: string | null;
  workingHours?: string | null;
  createdAt: string;
  updatedAt: string;
}

const MechanicGaragesPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState<District | undefined>(undefined);
  const [verifiedFilter, setVerifiedFilter] = useState<boolean | undefined>(undefined);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const { data: districts } = trpc.emergencyService.getDistricts.useQuery();
  
  const { data, isLoading, error, refetch } = trpc.emergencyService.getAllMechanicGarages.useQuery({
    page,
    pageSize,
    search: debouncedSearchQuery,
    district: selectedDistrict,
    isVerified: verifiedFilter,
  }, {
    refetchInterval: 5000
  });

  const garages = data?.garages || [];
  const totalGarages = data?.totalCount || 0;
  const totalPages = Math.ceil(totalGarages / pageSize);

  // Create mutation
  const createMutation = trpc.emergencyService.createMechanicGarage.useMutation({
    onSuccess: () => { toast.success("Mechanic garage created!"); refetch(); setIsCreateModalOpen(false); resetForm(); },
    onError: (err) => toast.error(err.message),
  });

  // Update mutation
  const updateMutation = trpc.emergencyService.updateMechanicGarage.useMutation({
    onSuccess: () => { toast.success("Mechanic garage updated!"); refetch(); setIsEditModalOpen(false); setEditingGarage(null); },
    onError: (err) => toast.error(err.message),
  });

  // Delete mutation
  const deleteMutation = trpc.emergencyService.deleteMechanicGarage.useMutation({
    onSuccess: () => { toast.success("Mechanic garage deleted!"); refetch(); },
    onError: (err) => toast.error(err.message),
  });

  // Form state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isMapPickerOpen, setIsMapPickerOpen] = useState(false);
  const [editingGarage, setEditingGarage] = useState<MechanicGarage | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    phoneTwo: '',
    email: '',
    address: '',
    district: District.Lilongwe,
    latitude: '',
    longitude: '',
    services: [] as string[],
    specialties: [] as string[],
    description: '',
    website: '',
    workingHours: '',
  });

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      phoneTwo: '',
      email: '',
      address: '',
      district: District.Lilongwe,
      latitude: '',
      longitude: '',
      services: [],
      specialties: [],
      description: '',
      website: '',
      workingHours: '',
    });
  };

  const handleCreate = () => {
    createMutation.mutate({
      ...formData,
      latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
      longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
    });
  };

  const handleUpdate = () => {
    if (!editingGarage) return;
    
    updateMutation.mutate({
      id: editingGarage.id,
      name: formData.name || undefined,
      phone: formData.phone || undefined,
      phoneTwo: formData.phoneTwo || null,
      email: formData.email || null,
      address: formData.address || null,
      district: formData.district,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      services: formData.services,
      specialties: formData.specialties,
      description: formData.description || null,
      website: formData.website || null,
      workingHours: formData.workingHours || null,
    });
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this mechanic garage?')) {
      deleteMutation.mutate({ id });
    }
  };

  const openEditModal = (garage: MechanicGarage) => {
    setEditingGarage(garage);
    setFormData({
      name: garage.name,
      phone: garage.phone,
      phoneTwo: garage.phoneTwo || '',
      email: garage.email || '',
      address: garage.address || '',
      district: garage.district,
      latitude: garage.latitude?.toString() || '',
      longitude: garage.longitude?.toString() || '',
      services: garage.services,
      specialties: garage.specialties,
      description: garage.description || '',
      website: garage.website || '',
      workingHours: garage.workingHours || '',
    });
    setIsEditModalOpen(true);
  };

  const availableServices = [
    'ENGINE_REPAIR', 'BRAKE_SERVICE', 'OIL_CHANGE', 'TRANSMISSION_REPAIR',
    'AIR_CONDITIONING', 'ELECTRICAL_REPAIR', 'SUSPENSION_WORK', 'TIRE_SERVICES',
    'DIAGNOSTICS', 'BATTERY_REPLACEMENT'
  ];

  const availableSpecialties = [
    'HEAVY_DUTY', 'LIGHT_VEHICLE', 'MOTORCYCLE', 'ELECTRICAL', 
    'HYBRID_ELECTRIC', 'DIESEL_ENGINE', 'IMPORT_CARS', 'FLEET_SERVICE'
  ];

  if (error) {
    return (
      <div className="container mx-auto py-12 px-4 max-w-7xl">
        <Alert variant="destructive">
          <Terminal className="h-4 w-4" />
          <AlertTitle>Error Loading Mechanic Garages</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-4 px-2 sm:py-12 sm:px-4 max-w-full animate-fade-in">
      <Card className="glass">
        <CardHeader className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-foreground text-lg sm:text-2xl">
                <Wrench className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                Mechanic Garages
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm mt-1">
                Manage registered mechanic garages visible on the public directory.
              </CardDescription>
            </div>
            <Button onClick={() => { resetForm(); setIsCreateModalOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Add Garage
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 sm:space-y-6 p-4 sm:p-6 pt-0 sm:pt-0">
          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-3 sm:gap-4">
            <Input
              placeholder="Search by name, phone, or address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full md:flex-1 bg-input text-foreground h-9 sm:h-10 text-sm"
            />
            <Select value={selectedDistrict} onValueChange={(value: District) => setSelectedDistrict(value)}>
              <SelectTrigger className="w-full md:w-[180px] bg-select text-foreground h-9 sm:h-10 text-xs sm:text-sm">
                <SelectValue placeholder="Filter by District" />
              </SelectTrigger>
              <SelectContent className="bg-popover text-popover-foreground">
                <SelectItem value={undefined as any}>All Districts</SelectItem>
                {districts?.map(district => <SelectItem key={district} value={district}>{district}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={verifiedFilter?.toString()} onValueChange={(value) => setVerifiedFilter(value === 'true' ? true : value === 'false' ? false : undefined)}>
              <SelectTrigger className="w-full md:w-[150px] bg-select text-foreground h-9 sm:h-10 text-xs sm:text-sm">
                <SelectValue placeholder="Verification" />
              </SelectTrigger>
              <SelectContent className="bg-popover text-popover-foreground">
                <SelectItem value={undefined as any}>All Status</SelectItem>
                <SelectItem value="true">Verified</SelectItem>
                <SelectItem value="false">Not Verified</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="h-9 sm:h-10" onClick={() => { setSearchQuery(''); setSelectedDistrict(undefined); setVerifiedFilter(undefined); }}>
              Reset
            </Button>
          </div>

          {/* Garages Table */}
          <div className="rounded-md border bg-card text-card-foreground shadow-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px] text-primary">Name</TableHead>
                  <TableHead className="w-[150px] text-primary">Contact</TableHead>
                  <TableHead className="w-[120px] text-primary">District</TableHead>
                  <TableHead className="w-[150px] text-primary">Specialties</TableHead>
                  <TableHead className="w-[100px] text-primary">Status</TableHead>
                  <TableHead className="w-[120px] text-primary">Added</TableHead>
                  <TableHead className="w-[150px] text-primary text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: pageSize }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={7}>
                        <Skeleton className="h-8 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : garages.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                      <p><InfoIcon className="inline-block mr-2 h-5 w-5" /> No mechanic garages found.</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  garages.map((garage) => (
                    <TableRow key={garage.id} className="text-sm">
                      <TableCell>
                        <p className="font-medium text-foreground">{garage.name}</p>
                        {garage.website && (
                          <p className="text-xs text-blue-500 flex items-center gap-1">
                            <Globe className="h-3 w-3" />
                            <a href={garage.website} target="_blank" rel="noopener noreferrer">{garage.website}</a>
                          </p>
                        )}
                        {garage.description && (
                          <p className="text-muted-foreground text-xs truncate max-w-[200px]">{garage.description}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="flex items-center gap-1 text-xs">
                            <Phone className="h-3 w-3" />
                            {garage.phone}
                          </p>
                          {garage.phoneTwo && (
                            <p className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Phone className="h-3 w-3" />
                              {garage.phoneTwo}
                            </p>
                          )}
                          {garage.email && (
                            <p className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Mail className="h-3 w-3" />
                              {garage.email}
                            </p>
                          )}
                          {garage.workingHours && (
                            <p className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              {garage.workingHours}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{garage.district}</Badge>
                        {garage.address && (
                          <p className="text-xs text-muted-foreground mt-1 truncate max-w-[150px]">{garage.address}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {garage.specialties.slice(0, 2).map(specialty => (
                            <Badge key={specialty} variant="secondary" className="text-xs">{specialty.replace(/_/g, ' ')}</Badge>
                          ))}
                          {garage.specialties.length > 2 && (
                            <Badge variant="secondary" className="text-xs">+{garage.specialties.length - 2}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge variant={garage.isActive ? "success" : "destructive"} className="text-xs">
                            {garage.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                          <Badge variant={garage.isVerified ? "success" : "warning"} className="text-xs">
                            {garage.isVerified ? 'Verified' : 'Pending'}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {format(new Date(garage.createdAt), 'yyyy-MM-dd')}
                      </TableCell>
                      <TableCell className="flex justify-center items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEditModal(garage)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => handleDelete(garage.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center pt-4">
            <p className="text-sm text-muted-foreground">
              Showing {Math.min(totalGarages, (page - 1) * pageSize + 1)} - {Math.min(totalGarages, page * pageSize)} of {totalGarages} garages
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(prev => Math.max(1, prev - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="h-4 w-4" /> Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(prev => prev + 1)}
                disabled={page >= totalPages}
              >
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="sm:max-w-xl bg-card text-card-foreground max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground">Add Mechanic Garage</DialogTitle>
            <DialogDescription>
              Enter details for the new mechanic garage.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">Name *</Label>
              <Input 
                id="name" 
                value={formData.name} 
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="col-span-3" 
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="phone" className="text-right">Phone *</Label>
              <Input 
                id="phone" 
                value={formData.phone} 
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="col-span-3" 
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="phoneTwo" className="text-right">Phone 2</Label>
              <Input 
                id="phoneTwo" 
                value={formData.phoneTwo} 
                onChange={(e) => setFormData({...formData, phoneTwo: e.target.value})}
                className="col-span-3" 
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="email" className="text-right">Email</Label>
              <Input 
                id="email" 
                type="email"
                value={formData.email} 
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="col-span-3" 
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="website" className="text-right">Website</Label>
              <Input 
                id="website" 
                value={formData.website} 
                onChange={(e) => setFormData({...formData, website: e.target.value})}
                className="col-span-3" 
                placeholder="https://..."
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="district" className="text-right">District *</Label>
              <Select 
                value={formData.district} 
                onValueChange={(value: District) => setFormData({...formData, district: value})}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {districts?.map(district => <SelectItem key={district} value={district}>{district}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="address" className="text-right">Address</Label>
              <Input 
                id="address" 
                value={formData.address} 
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="col-span-3" 
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label className="text-right">Location</Label>
              <div className="col-span-3 flex gap-2">
                <Input 
                  id="latitude" 
                  type="number"
                  step="any"
                  placeholder="Latitude"
                  value={formData.latitude} 
                  onChange={(e) => setFormData({...formData, latitude: e.target.value})}
                  className="flex-1" 
                />
                <Input 
                  id="longitude" 
                  type="number"
                  step="any"
                  placeholder="Longitude"
                  value={formData.longitude} 
                  onChange={(e) => setFormData({...formData, longitude: e.target.value})}
                  className="flex-1" 
                />
                <Button 
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setIsMapPickerOpen(true)}
                  title="Pick on map"
                >
                  <MapPin className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="workingHours" className="text-right">Working Hours</Label>
              <Input 
                id="workingHours" 
                value={formData.workingHours} 
                onChange={(e) => setFormData({...formData, workingHours: e.target.value})}
                className="col-span-3" 
                placeholder="Mon-Fri: 8AM-6PM"
              />
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <Label className="text-right pt-2">Specialties</Label>
              <div className="col-span-3 flex flex-wrap gap-2">
                {availableSpecialties.map(specialty => (
                  <Badge 
                    key={specialty}
                    variant={formData.specialties.includes(specialty) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        specialties: prev.specialties.includes(specialty) 
                          ? prev.specialties.filter(s => s !== specialty)
                          : [...prev.specialties, specialty]
                      }));
                    }}
                  >
                    {specialty.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <Label className="text-right pt-2">Services</Label>
              <div className="col-span-3 flex flex-wrap gap-2">
                {availableServices.map(service => (
                  <Badge 
                    key={service}
                    variant={formData.services.includes(service) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        services: prev.services.includes(service) 
                          ? prev.services.filter(s => s !== service)
                          : [...prev.services, service]
                      }));
                    }}
                  >
                    {service.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <Label htmlFor="description" className="text-right pt-2">Description</Label>
              <textarea 
                id="description" 
                value={formData.description} 
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="col-span-3 min-h-[80px] p-2 rounded-md border bg-background text-foreground"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={createMutation.isLoading || !formData.name || !formData.phone}>
              {createMutation.isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Create Garage'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Map Picker */}
      <MapPicker
        isOpen={isMapPickerOpen}
        onClose={() => setIsMapPickerOpen(false)}
        onSelect={(lat, lng) => {
          setFormData(prev => ({
            ...prev,
            latitude: lat.toString(),
            longitude: lng.toString()
          }));
        }}
        initialLat={formData.latitude ? parseFloat(formData.latitude) : undefined}
        initialLng={formData.longitude ? parseFloat(formData.longitude) : undefined}
      />

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-xl bg-card text-card-foreground max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground">Edit Mechanic Garage</DialogTitle>
            <DialogDescription>
              Update details for {editingGarage?.name}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-name" className="text-right">Name</Label>
              <Input 
                id="edit-name" 
                value={formData.name} 
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="col-span-3" 
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-phone" className="text-right">Phone</Label>
              <Input 
                id="edit-phone" 
                value={formData.phone} 
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="col-span-3" 
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-phoneTwo" className="text-right">Phone 2</Label>
              <Input 
                id="edit-phoneTwo" 
                value={formData.phoneTwo} 
                onChange={(e) => setFormData({...formData, phoneTwo: e.target.value})}
                className="col-span-3" 
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-email" className="text-right">Email</Label>
              <Input 
                id="edit-email" 
                type="email"
                value={formData.email} 
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="col-span-3" 
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-website" className="text-right">Website</Label>
              <Input 
                id="edit-website" 
                value={formData.website} 
                onChange={(e) => setFormData({...formData, website: e.target.value})}
                className="col-span-3" 
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-district" className="text-right">District</Label>
              <Select 
                value={formData.district} 
                onValueChange={(value: District) => setFormData({...formData, district: value})}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {districts?.map(district => <SelectItem key={district} value={district}>{district}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-address" className="text-right">Address</Label>
              <Input 
                id="edit-address" 
                value={formData.address} 
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="col-span-3" 
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label className="text-right">Location</Label>
              <div className="col-span-3 flex gap-2">
                <Input 
                  id="edit-latitude" 
                  type="number"
                  step="any"
                  placeholder="Latitude"
                  value={formData.latitude} 
                  onChange={(e) => setFormData({...formData, latitude: e.target.value})}
                  className="flex-1" 
                />
                <Input 
                  id="edit-longitude" 
                  type="number"
                  step="any"
                  placeholder="Longitude"
                  value={formData.longitude} 
                  onChange={(e) => setFormData({...formData, longitude: e.target.value})}
                  className="flex-1" 
                />
                <Button 
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setIsMapPickerOpen(true)}
                  title="Pick on map"
                >
                  <MapPin className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-workingHours" className="text-right">Working Hours</Label>
              <Input 
                id="edit-workingHours" 
                value={formData.workingHours} 
                onChange={(e) => setFormData({...formData, workingHours: e.target.value})}
                className="col-span-3" 
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label className="text-right">Status</Label>
              <div className="col-span-3 flex flex-wrap gap-2">
                <Button
                  variant={editingGarage?.isActive ? "default" : "outline"}
                  size="sm"
                  onClick={() => editingGarage && updateMutation.mutate({ id: editingGarage.id, isActive: true })}
                >
                  <CheckCircle className="h-4 w-4 mr-1" /> Active
                </Button>
                <Button
                  variant={!editingGarage?.isActive ? "destructive" : "outline"}
                  size="sm"
                  onClick={() => editingGarage && updateMutation.mutate({ id: editingGarage.id, isActive: false })}
                >
                  <XCircle className="h-4 w-4 mr-1" /> Inactive
                </Button>
                <Button
                  variant={editingGarage?.isVerified ? "success" : "outline"}
                  size="sm"
                  onClick={() => editingGarage && updateMutation.mutate({ id: editingGarage.id, isVerified: !editingGarage.isVerified })}
                >
                  <CheckCircle className="h-4 w-4 mr-1" /> {editingGarage?.isVerified ? 'Verified' : 'Verify'}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <Label className="text-right pt-2">Specialties</Label>
              <div className="col-span-3 flex flex-wrap gap-2">
                {availableSpecialties.map(specialty => (
                  <Badge 
                    key={specialty}
                    variant={formData.specialties.includes(specialty) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        specialties: prev.specialties.includes(specialty) 
                          ? prev.specialties.filter(s => s !== specialty)
                          : [...prev.specialties, specialty]
                      }));
                    }}
                  >
                    {specialty.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <Label className="text-right pt-2">Services</Label>
              <div className="col-span-3 flex flex-wrap gap-2">
                {availableServices.map(service => (
                  <Badge 
                    key={service}
                    variant={formData.services.includes(service) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        services: prev.services.includes(service) 
                          ? prev.services.filter(s => s !== service)
                          : [...prev.services, service]
                      }));
                    }}
                  >
                    {service.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <Label htmlFor="edit-description" className="text-right pt-2">Description</Label>
              <textarea 
                id="edit-description" 
                value={formData.description} 
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="col-span-3 min-h-[80px] p-2 rounded-md border bg-background text-foreground"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isLoading}>
              {updateMutation.isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MechanicGaragesPage;
